import type {
  ConversationRef,
  ConversationSummary,
  CreateConversationOptions,
  Language,
  PagePayload,
  PageState,
  RemoteMessage,
  SendMessageOptions,
  Skill,
  SkillGroup,
  Turn,
} from '../types';

/** Volto names the root page ""; the agent API persists the same page as "/". */
export const canonicalPagePathname = (pathname?: string | null): string =>
  pathname || '/';

/**
 * Fixed backend profile matching the renderers and schemas in bare Volto. The
 * broader `vanilla-generic` profile also contains add-on-only blocks and must
 * not be used by this add-on.
 */
export const AGENT_VERSION = 'ai-editor';

/**
 * What the assistant UI is allowed to know about its backend.
 *
 * Every client — the real one, the offline mock, a customer's own — is a
 * subclass of this. The UI holds one instance and never branches on which:
 * anything a particular backend does differently belongs behind these
 * methods, not in a caller's `if`.
 *
 * The active conversation lives here rather than in the UI because it
 * outlives any panel: the panel decides *when* a conversation starts, the
 * client remembers *which* one is running.
 */
export abstract class AgentClient {
  protected current: ConversationRef | null = null;

  /**
   * The page payload the agent works on: the block tree plus the metadata
   * fields the backend models. Everything else in Volto's content and form
   * data — services, expansions, workflow noise — stays client-side.
   *
   * Override to send more of the page than the stock backend models; the
   * agent ignores fields its schema does not know, so adding one is safe
   * and dropping one is not.
   */
  protected pagePayload(state: PageState, pathname: string): PagePayload {
    const payload: PagePayload = {
      link: canonicalPagePathname(pathname),
      blocks: state?.blocks || {},
      blocks_layout: state?.blocks_layout || { items: [] },
    };
    (
      ['title', 'description', 'preview_image', 'review_state'] as const
    ).forEach((key) => {
      if (typeof state?.[key] === 'string') payload[key] = state[key] as string;
    });
    if (Array.isArray(state?.subjects)) payload.subjects = state.subjects;

    return payload;
  }

  /** The conversation this client is currently running, if any. */
  get activeConversation(): ConversationRef | null {
    return this.current;
  }

  /** Adopt an existing conversation, e.g. one picked from the history. */
  activateConversation(ref: {
    conversationId: string;
    pathname: string;
    language: Language;
  }): void {
    this.current = { ...ref, pathname: canonicalPagePathname(ref.pathname) };
  }

  /** The skills this backend offers. An empty list is a valid answer. */
  abstract listSkills(): Promise<Skill[]>;

  /** The groups the skills are sorted into. An empty list is a valid answer. */
  abstract listSkillGroups(): Promise<SkillGroup[]>;

  abstract createConversation(
    options: CreateConversationOptions,
  ): Promise<{ conversationId: string }>;

  abstract listConversations(options?: {
    pathname?: string;
  }): Promise<ConversationSummary[]>;

  abstract getConversationMessages(
    conversationId: string,
  ): Promise<RemoteMessage[]>;

  abstract renameConversation(
    conversationId: string,
    title: string,
  ): Promise<ConversationSummary>;

  abstract deleteConversation(conversationId: string): Promise<unknown>;

  /** Start a turn. Returns immediately; progress arrives through the callbacks. */
  abstract sendMessage(options: SendMessageOptions): Turn;
}
